// frontend/src/utils/analytics.js

export const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID;

// A/B variant, Pass 2: no random assignment yet — everyone is 'control'.
// REACT_APP_AB_VARIANT wins when set (e.g. for local testing); otherwise a
// value persisted in localStorage under this key; otherwise 'control'.
const AB_VARIANT_STORAGE_KEY = 'circledin_ab_variant';

export const getVariant = () => {
  if (process.env.REACT_APP_AB_VARIANT) return process.env.REACT_APP_AB_VARIANT;
  try {
    const stored = window.localStorage.getItem(AB_VARIANT_STORAGE_KEY);
    if (stored) return stored;
  } catch (e) {
    // localStorage unavailable (private mode, disabled cookies, SSR) — fall through
  }
  return 'control';
};

// Check if GA is loaded and properly configured
export const isGALoaded = () => {
  return typeof window !== 'undefined' &&
         window.gtag &&
         GA_TRACKING_ID &&
         GA_TRACKING_ID !== 'undefined' &&
         !GA_TRACKING_ID.includes('%REACT_APP_GA_TRACKING_ID%');
};

// Track page views
export const trackPageView = (path, title) => {
  if (isGALoaded()) {
    window.gtag('config', GA_TRACKING_ID, {
      page_path: path,
      page_title: title,
    });
    console.log('📊 GA: Page view tracked -', title, path);
  } else {
    console.log('❌ GA: Not loaded, skipping page view tracking for', path);
  }
  trackView(path);
};

// Track the 8 funnel events (CLAUDE.md Analytics section), each carrying the
// A/B variant. Every emitter below funnels through this.
export const trackFunnel = (event, params = {}) => {
  if (isGALoaded()) {
    window.gtag('event', event, { ...params, variant: getVariant() });
  } else {
    console.log('❌ GA: Not loaded, skipping funnel event tracking for', event);
  }
};

export const trackView = (path) => trackFunnel('view', { path });

// The home-page carrier pick and the reservation form mount both call
// trackStarted for the same carrier; dedupe per session per carrier so
// `started` fires once per carrier per visit.
const STARTED_SESSION_KEY_PREFIX = 'circledin_started_';

export const trackStarted = (carrier) => {
  if (!carrier) return; // never record a placeholder carrier
  try {
    const key = STARTED_SESSION_KEY_PREFIX + String(carrier);
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, '1');
  } catch (e) {
    // sessionStorage unavailable (private mode, disabled cookies, SSR) — fall through and track
  }
  trackFunnel('started', { carrier });
};
export const trackSawNumber = (carrier, bill) => trackFunnel('saw_number', { carrier, bill });
export const trackLead = (circleId) => trackFunnel('lead', { circleId });
export const trackCommitted = () => trackFunnel('committed', {});
export const trackPaid = (orderId, amount) => trackFunnel('paid', { orderId, amount });
export const trackShared = (circleId, channel) => trackFunnel('shared', { circleId, channel });
export const trackHostStarted = (carrier) => trackFunnel('host_started', { carrier });

// Track custom events
export const trackEvent = (action, category, label, value) => {
  if (isGALoaded()) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
    console.log('📊 GA: Event tracked -', action, category, label);
  } else {
    console.log('❌ GA: Not loaded, skipping event tracking for', action);
  }
};

// Common tracking functions for CircledIn
export const trackClick = (elementName, location) => {
  trackEvent('click', 'engagement', `${elementName}_${location}`);
};

export const trackFormSubmit = (formName) => {
  trackEvent('form_submit', 'engagement', formName);
};

export const trackNavigation = (destination) => {
  trackEvent('navigation', 'user_flow', destination);
};

export const trackCircleJoin = (circleId, planType) => {
  trackEvent('circle_join', 'conversion', `circle_${circleId}_${planType}`, 1);
};

export const trackPayment = (amount, paymentType) => {
  trackEvent('purchase', 'conversion', paymentType, amount);
};

export const trackAuth = (action) => {
  trackEvent('auth_action', 'user_engagement', action);
};

export const trackCarrierSelection = (carrier) => {
  trackEvent('carrier_selected', 'user_preference', carrier);
};

export const trackPlanSelection = (planName, carrier) => {
  trackEvent('plan_selected', 'user_preference', `${carrier}_${planName}`);
};

export const trackReferral = (action, code) => {
  trackEvent('referral_action', 'growth', `${action}_${code}`);
};

// Debug function to test GA setup
export const testGA = () => {
  console.log('=== GA Debug Info ===');
  console.log('GA_TRACKING_ID:', GA_TRACKING_ID);
  console.log('window.gtag exists:', typeof window.gtag);
  console.log('window.dataLayer exists:', Array.isArray(window.dataLayer));
  console.log('isGALoaded():', isGALoaded());
  
  if (isGALoaded()) {
    trackEvent('test_event', 'debug', 'manual_test', 1);
    console.log('✅ Test event sent to GA');
    return true;
  } else {
    console.log('❌ GA not properly loaded');
    return false;
  }
};

// Get GA status for debugging
export const getGAStatus = () => {
  return {
    trackingId: GA_TRACKING_ID,
    gtagLoaded: typeof window.gtag !== 'undefined',
    dataLayerExists: typeof window.dataLayer !== 'undefined',
    isLoaded: isGALoaded()
  };
};